/**
 * currentUser — the signed-in dispatcher, resolved once and reused wherever we
 * stamp "who did this" (internal notes, recorded payments, audited edits). The
 * friendly FIRST name comes from the auth display name (user_metadata.full_name,
 * the same source the user menu shows); we fall back to a tidied email handle.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface CurrentUser {
  email: string | null;
  fullName: string | null;
  firstName: string | null;
  /** Linked team member (app_user.empno → mirror_employee), if associated. */
  empno: string | null;
  memberName: string | null;
}

/** First name for stamping: first token of the display name, else a tidied email local-part. */
export function firstNameFrom(
  fullName: string | null | undefined,
  email: string | null | undefined,
): string | null {
  const fn = (fullName ?? "").trim();
  if (fn) return fn.split(/\s+/)[0];
  const local = (email ?? "").split("@")[0];
  if (!local) return null;
  const base = local.split(/[._-]/)[0].replace(/\d+$/, "");
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : null;
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ["current_user_profile"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<CurrentUser> => {
      const { data } = await supabase.auth.getUser();
      const u = data?.user;
      const email = u?.email ?? null;
      const meta = (u?.user_metadata ?? {}) as Record<string, unknown>;
      const fullName = (meta.full_name as string) ?? (meta.name as string) ?? null;

      // Prefer the linked TEAM MEMBER's real name (app_user.empno → mirror_employee).
      // This is what an admin sets under Settings → Team; until then we fall back
      // to the auth display name, then a tidied email handle.
      let empno: string | null = null;
      let memberName: string | null = null;
      let memberFirst: string | null = null;
      if (u?.id) {
        const { data: au } = await supabase
          .from("app_user").select("empno").eq("user_id", u.id).maybeSingle();
        empno = (au as { empno?: string } | null)?.empno ?? null;
        if (empno) {
          const { data: emp } = await supabase
            .from("mirror_employee").select("firstname, empname").eq("empno", empno).maybeSingle();
          memberFirst = (emp as { firstname?: string } | null)?.firstname ?? null;
          memberName = (emp as { empname?: string } | null)?.empname ?? null;
        }
      }
      const firstName = (memberFirst && memberFirst.trim()) || firstNameFrom(fullName, email);
      return { email, fullName, firstName, empno, memberName };
    },
  });
}
